import { SeatData } from './SeatDot';
import { TableData } from './TableCircle';
import { ZONE_COLORS, STATUS_COLORS, BG_CARD, BORDER_LIGHT, TEXT_DARK, TEXT_SECONDARY, TEXT_MUTED } from './designTokens';

interface FloorplanTooltipProps {
  table: TableData | null;
  seats: SeatData[];
  position: { x: number; y: number };
  visible: boolean;
}

const SEAT_STATUS_COLORS: Record<string, string> = {
  Available: '#2e7d32',
  Assigned: '#1565c0',
  CheckedIn: '#6a1b9a',
  Held: '#e65100',
  Blocked: '#37474f',
};

export function FloorplanTooltip({ table, seats, position, visible }: FloorplanTooltipProps) {
  if (!visible || !table) return null;

  const zoneColor = ZONE_COLORS[table.zone.name] || table.zone.colorCode;
  const statusColor = STATUS_COLORS[table.status] || '#999';

  // Adjust position to stay in viewport
  const tooltipWidth = 240;
  const tooltipMaxHeight = 300;
  let left = position.x + 12;
  let top = position.y + 12;

  if (typeof window !== 'undefined') {
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = position.x - tooltipWidth - 12;
    }
    if (top + tooltipMaxHeight > window.innerHeight - 16) {
      top = position.y - tooltipMaxHeight - 12;
    }
    if (left < 16) left = 16;
    if (top < 16) top = 16;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left,
        top,
        width: tooltipWidth,
        maxHeight: tooltipMaxHeight,
        overflowY: 'auto',
        backgroundColor: BG_CARD,
        border: `1px solid ${BORDER_LIGHT}`,
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        padding: '0.75rem',
        zIndex: 1100,
        pointerEvents: 'none',
        fontSize: '0.8rem',
      }}
      role="tooltip"
    >
      {/* Header */}
      <div style={{ marginBottom: '0.5rem', borderBottom: `1px solid ${BORDER_LIGHT}`, paddingBottom: '0.5rem' }}>
        <div style={{ fontWeight: 700, color: TEXT_DARK, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
          {table.tableName}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-block',
              padding: '0.1rem 0.4rem',
              borderRadius: '3px',
              backgroundColor: zoneColor,
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 600,
            }}
          >
            {table.zone.name}
          </span>
          <span
            style={{
              display: 'inline-block',
              padding: '0.1rem 0.4rem',
              borderRadius: '3px',
              backgroundColor: statusColor,
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 600,
            }}
          >
            {table.status}
          </span>
        </div>
      </div>

      {/* Seat list */}
      {seats.length > 0 ? (
        <div>
          {seats.map((seat) => (
            <div
              key={seat.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.2rem 0',
                borderBottom: `1px solid ${BORDER_LIGHT}`,
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: SEAT_STATUS_COLORS[seat.status] || '#999',
                  flexShrink: 0,
                }}
              />
              <span style={{ color: TEXT_MUTED, fontSize: '0.7rem', minWidth: '18px' }}>
                #{seat.seatNumber}
              </span>
              <span style={{ color: TEXT_SECONDARY, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {seat.guestName || 'Empty'}
              </span>
              {seat.protocolVip && (
                <span style={{ fontSize: '0.7rem' }} title="VIP">⭐</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: TEXT_MUTED, fontStyle: 'italic' }}>Loading seat details...</div>
      )}
    </div>
  );
}
